import { CheckCircle, DollarSign, Clock, Home, AlertCircle, Users } from 'lucide-react';
import CTAButton from '../components/CTAButton';
import { Link } from 'react-router-dom';

export default function HomePage() {
  return (
    <div className="min-h-screen">
      <section className="pt-12 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                <span className="text-blue-900 font-extrabold">Quick Close Home Offers</span> — Sell Your House Quickly and Easily
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                No repairs needed. No commissions. No waiting around. Get a fair cash offer and move on to your next chapter.
              </p>
              <div className="flex flex-wrap gap-4">
                <CTAButton text="Get My Cash Offer" size="lg" />
                <Link
                  to="/how-it-works"
                  className="inline-block bg-white text-blue-900 px-10 py-4 rounded-lg hover:shadow-lg transition-all border-2 border-blue-900 text-lg font-semibold"
                >
                  See How It Works
                </Link>
              </div>
            </div>
            <div className="relative">
              <img
                src="/img_5078.jpg"
                alt="Louisville Kentucky Skyline"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-5 gap-6 text-center">
            <div className="flex flex-col items-center">
              <DollarSign className="w-10 h-10 text-blue-900 mb-3" />
              <div className="text-sm font-semibold text-gray-600">No Repairs</div>
              <div className="text-xs text-gray-500">Sell as-is</div>
            </div>
            <div className="flex flex-col items-center">
              <CheckCircle className="w-10 h-10 text-blue-900 mb-3" />
              <div className="text-sm font-semibold text-gray-600">No Commissions</div>
              <div className="text-xs text-gray-500">Zero agent fees</div>
            </div>
            <div className="flex flex-col items-center">
              <Clock className="w-10 h-10 text-blue-900 mb-3" />
              <div className="text-sm font-semibold text-gray-600">We Pay Closing</div>
              <div className="text-xs text-gray-500">All costs covered</div>
            </div>
            <div className="flex flex-col items-center">
              <Home className="w-10 h-10 text-blue-900 mb-3" />
              <div className="text-sm font-semibold text-gray-600">Cash Offer</div>
              <div className="text-xs text-gray-500">No financing needed</div>
            </div>
            <div className="flex flex-col items-center">
              <AlertCircle className="w-10 h-10 text-blue-900 mb-3" />
              <div className="text-sm font-semibold text-gray-600">Your Timeline</div>
              <div className="text-xs text-gray-500">Close when ready</div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Situations We Help With</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Whether your situation is simple or complex, we're here to help you sell with confidence.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Clock, title: 'Need to Sell Fast', desc: 'Job change, time-sensitive situations, or just ready to move on?' },
              { icon: AlertCircle, title: 'Facing Foreclosure', desc: 'We can help you avoid foreclosure and protect your credit.' },
              { icon: Home, title: 'Inherited Property', desc: 'Inherited a house? We simplify the selling process.' },
              { icon: Users, title: 'Major Repairs Needed', desc: 'Sell as-is without expensive renovations.' },
              { icon: DollarSign, title: 'Going Through Divorce', desc: 'Need a clean, quick sale to move forward.' },
              { icon: CheckCircle, title: 'Bad Tenant / Landlord', desc: 'Tired of being a landlord? We buy rental properties too.' }
            ].map((item, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md border border-gray-100 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-blue-900" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-blue-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">How It Works</h2>
          <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto">
            Our process is simple, transparent, and built around your timeline.
          </p>
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            {[
              { num: '1', title: 'Tell Us About Your Property', desc: 'Share basic information about your home' },
              { num: '2', title: 'Get a Cash Offer', desc: 'We review and prepare a fair offer' },
              { num: '3', title: 'Quick Walkthrough', desc: 'Scheduled on your time (no obligation)' },
              { num: '4', title: 'Close & Get Paid', desc: 'You pick the closing date' }
            ].map((step, index) => (
              <div key={index} className="relative">
                <div className="text-7xl font-black text-blue-900 mb-3 drop-shadow-lg">{step.num}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-600 text-sm">{step.desc}</p>
              </div>
            ))}
          </div>
          <Link
            to="/how-it-works"
            className="inline-block text-blue-900 font-semibold hover:text-blue-950 transition-colors"
          >
            Learn more about our process →
          </Link>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Quick Close?</h2>
          <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto">
            We're not a traditional real estate agent. We're a direct home buyer committed to making selling simple, private, and stress-free.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <CheckCircle className="w-8 h-8 text-blue-900 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">No Pressure</h3>
              <p className="text-gray-600">
                Requesting an offer is completely free and obligation-free. You're in control.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <CheckCircle className="w-8 h-8 text-blue-900 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Transparent & Simple</h3>
              <p className="text-gray-600">
                No hidden fees, no surprises. You'll know exactly what to expect.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <CheckCircle className="w-8 h-8 text-blue-900 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Respect Your Timeline</h3>
              <p className="text-gray-600">
                Sell on your schedule. You decide when and how fast you want to close.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-900 to-blue-950">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl mb-8 text-blue-50 max-w-2xl mx-auto">
            Getting a cash offer is easy, free, and takes just a few minutes. No obligation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <CTAButton text="Get My Cash Offer" size="lg" variant="primary" />
            <a
              href="tel:412-284-6681"
              className="inline-block bg-white text-blue-900 px-10 py-4 rounded-lg hover:shadow-lg transition-all text-lg font-semibold"
            >
              Call Now: 412-284-6681
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
